import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../FirebaseAppContext';
import { HybridIcon } from '../components/HybridIcon';
import type { ServiceLog } from '../models';
import './Services.css';

function getNextServiceTone(service: ServiceLog): 'pill-danger' | 'pill-warn' | 'pill-good' | 'pill-accent' {
  if (!service.nextServiceKm) {
    return 'pill-accent';
  }

  if (service.mileage >= service.nextServiceKm) {
    return 'pill-danger';
  }

  if (service.nextServiceKm - service.mileage <= 1000) {
    return 'pill-warn';
  }

  return 'pill-good';
}

export default function Services() {
  const { state } = useAppContext();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const services = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return state.services;
    }

    return state.services.filter((service) => {
      const car = state.cars.find((currentCar) => currentCar.id === service.carId);
      return (
        (service.description || '').toLowerCase().includes(q) ||
        (service.date || '').toLowerCase().includes(q) ||
        String(service.mileage || '').includes(q) ||
        String(service.nextServiceKm || '').includes(q) ||
        (car?.licensePlate || '').toLowerCase().includes(q) ||
        (car?.make || '').toLowerCase().includes(q) ||
        (car?.model || '').toLowerCase().includes(q)
      );
    });
  }, [query, state.cars, state.services]);

  const stats = useMemo(() => {
    const dueNow = services.filter((service) => service.nextServiceKm && service.mileage >= service.nextServiceKm).length;
    const dueSoon = services.filter((service) => service.nextServiceKm && service.nextServiceKm - service.mileage <= 1000 && service.mileage < service.nextServiceKm).length;

    return {
      total: services.length,
      dueNow,
      dueSoon,
    };
  }, [services]);

  return (
    <div className="services-page">
      <section className="services-hero card">
        <div>
          <p className="eyebrow">Maintenance</p>
          <h1 className="services-title">Service Logs</h1>
          <p className="services-subtitle">Track completed work, spot upcoming service windows, and keep your fleet healthy.</p>
        </div>
        <button className="btn btn-primary services-add" onClick={() => navigate('/services/new')}>
          <HybridIcon name="add-circle" size={18} color="currentColor" />
          Add Service Log
        </button>
      </section>

      <section className="services-metrics">
        <article className="metric">
          <span>Logs shown</span>
          <strong>{stats.total}</strong>
        </article>
        <article className="metric">
          <span>Due now</span>
          <strong>{stats.dueNow}</strong>
        </article>
        <article className="metric">
          <span>Due soon</span>
          <strong>{stats.dueSoon}</strong>
        </article>
      </section>

      <section className="services-toolbar card">
        <HybridIcon name="search" size={18} color="var(--ink-3)" />
        <input
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by description, plate, vehicle, date, or km"
          className="services-search-input"
        />
        {query.length > 0 && (
          <button className="btn btn-secondary services-clear" onClick={() => setQuery('')} type="button">
            Clear
          </button>
        )}
      </section>

      <section className="services-list">
        {services.length === 0 ? (
          <div className="card services-empty">
            <HybridIcon name="construct-outline" size={44} color="var(--ink-3)" />
            <h3>No service logs found</h3>
            <p>Try a different query or create your first service log.</p>
          </div>
        ) : (
          services.map((service) => {
            const car = state.cars.find((currentCar) => currentCar.id === service.carId);
            const tone = getNextServiceTone(service);

            return (
              <button key={service.id} className="lrow services-row" onClick={() => navigate(`/services/${service.id}`)} type="button">
                <div className="car-thumb">
                  <HybridIcon name="build" size={20} color="var(--accent)" />
                </div>
                <div className="services-main">
                  <div className="services-line1">
                    <strong>{service.description}</strong>
                    <span className={`pill ${tone}`}>
                      {service.nextServiceKm ? `Next ${service.nextServiceKm.toLocaleString()} km` : 'No next service set'}
                    </span>
                  </div>
                  <div className="services-line2">
                    {car ? `${car.year} ${car.make} ${car.model} (${car.licensePlate})` : 'Unknown vehicle'}
                  </div>
                  <div className="services-line3">
                    {new Date(service.date).toLocaleDateString()} · {service.mileage.toLocaleString()} km
                  </div>
                </div>
                <HybridIcon name="chevron-forward" size={18} color="var(--ink-3)" />
              </button>
            );
          })
        )}
      </section>
    </div>
  );
}
